/**
 * ResetParentPasswordModal — сброс пароля родителя администратором.
 *
 * Использует существующий endpoint POST /api/users/{user_id}/reset-password
 * с require_change=true, чтобы при следующем входе в ЛК родитель прошёл
 * принудительный flow смены пароля.
 *
 * Минимум: 8 символов, буквы + цифры (как при регистрации).
 * После успешного сброса показывает новый пароль один раз с CopyButton —
 * админ должен скопировать и передать родителю любым каналом.
 */
import React, { useState } from 'react';
import {
  Modal, Stack, Group, Text, Button, PasswordInput, Box, Alert,
  CopyButton, Tooltip, ActionIcon, TextInput,
} from '@mantine/core';
import { IconKey, IconRefresh, IconAlertCircle, IconCheck, IconCopy } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import tokens from '../../theme/designTokens';

const MIN_PASSWORD_LENGTH = 8;
const RANDOM_PASSWORD_LENGTH = 12;
const RANDOM_PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';

function generateRandomPassword() {
  let pw = '';
  const buf = new Uint32Array(RANDOM_PASSWORD_LENGTH);
  crypto.getRandomValues(buf);
  for (let i = 0; i < RANDOM_PASSWORD_LENGTH; i++) {
    pw += RANDOM_PASSWORD_ALPHABET[buf[i] % RANDOM_PASSWORD_ALPHABET.length];
  }
  // Гарантируем хотя бы 1 букву и 1 цифру
  if (!/[a-zA-Z]/.test(pw)) pw = 'A' + pw.slice(1);
  if (!/\d/.test(pw)) pw = pw.slice(0, -1) + '7';
  return pw;
}

function validatePassword(value) {
  if (!value || value.length < MIN_PASSWORD_LENGTH) {
    return `Пароль должен быть не менее ${MIN_PASSWORD_LENGTH} символов`;
  }
  if (!/[a-zA-Zа-яА-Я]/.test(value)) {
    return 'Пароль должен содержать хотя бы одну букву';
  }
  if (!/\d/.test(value)) {
    return 'Пароль должен содержать хотя бы одну цифру';
  }
  return null;
}

const ResetParentPasswordModal = ({ opened, onClose, parent }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultPassword, setResultPassword] = useState(null); // показываем после успеха

  const reset = () => {
    setPassword('');
    setConfirm('');
    setResultPassword(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleGenerate = () => {
    const pw = generateRandomPassword();
    setPassword(pw);
    setConfirm(pw);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const err = validatePassword(password);
    if (err) {
      notifications.show({ title: 'Ошибка', message: err, color: 'red' });
      return;
    }
    if (password !== confirm) {
      notifications.show({ title: 'Ошибка', message: 'Пароли не совпадают', color: 'red' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/users/${parent.id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          new_password: password,
          require_change: true,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Не удалось сбросить пароль');
      }
      setResultPassword(password);
      notifications.show({
        title: 'Пароль сброшен',
        message: 'Передайте новый пароль родителю — при входе он будет обязан его сменить.',
        color: 'green',
        icon: <IconCheck size={16} />,
        autoClose: 7000,
      });
    } catch (e2) {
      notifications.show({ title: 'Ошибка', message: e2.message, color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!parent) return null;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconKey size={20} color={tokens.colors.brand.primary} />
          <Text fw={600}>Сброс пароля родителя</Text>
        </Group>
      }
      size="sm"
      centered
    >
      <Stack gap="md">
        <Box>
          <Text size="sm" c="dimmed">Родитель</Text>
          <Text fw={500}>{parent.full_name || parent.email}</Text>
          {parent.full_name && (
            <Text size="xs" c="dimmed">{parent.email}</Text>
          )}
        </Box>

        {resultPassword ? (
          // Экран успеха: показываем пароль один раз
          <>
            <Alert color="green" radius="md" icon={<IconCheck size={18} />}>
              Пароль успешно сброшен. При следующем входе в ЛК родитель будет обязан задать новый пароль.
            </Alert>

            <Box>
              <Text size="xs" c="dimmed" mb={4}>Новый пароль (скопируйте и передайте родителю)</Text>
              <Group gap="xs">
                <TextInput
                  value={resultPassword}
                  readOnly
                  size="md"
                  radius="md"
                  style={{ flex: 1 }}
                  styles={{ input: { fontFamily: 'monospace', fontWeight: 600 } }}
                />
                <CopyButton value={resultPassword}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Скопировано' : 'Скопировать'}>
                      <ActionIcon variant="filled" color="blue" onClick={copy} size="lg">
                        {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
              <Text size="xs" c="dimmed" mt={4}>
                После закрытия окна пароль увидеть снова будет нельзя.
              </Text>
            </Box>

            <Group justify="flex-end">
              <Button onClick={handleClose}>Закрыть</Button>
            </Group>
          </>
        ) : (
          // Форма ввода нового пароля
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <Alert color="orange" radius="md" icon={<IconAlertCircle size={18} />}>
                После сброса родителю придётся войти по этому паролю и сразу его сменить.
              </Alert>

              <Box>
                <Group justify="space-between" align="center" mb={4}>
                  <Text size="sm" fw={500}>Новый пароль</Text>
                  <Button
                    variant="subtle"
                    size="compact-xs"
                    leftSection={<IconRefresh size={12} />}
                    onClick={handleGenerate}
                  >
                    Сгенерировать
                  </Button>
                </Group>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Минимум 8 символов, буквы и цифры"
                  required
                  size="md"
                  radius="md"
                />
              </Box>

              <PasswordInput
                label="Повторите пароль"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                required
                size="md"
                radius="md"
              />

              <Group justify="flex-end" gap="sm">
                <Button variant="subtle" color="gray" onClick={handleClose} type="button">
                  Отмена
                </Button>
                <Button type="submit" loading={submitting} color="red">
                  Сбросить пароль
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Stack>
    </Modal>
  );
};

export default ResetParentPasswordModal;
